import { Link } from 'react-router-dom';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import { History, Mail, Phone, User, Wallet } from 'lucide-react';
import type { AgentPlayerDetail, AgentPlayerWalletTransaction } from '../../types';

type Props = {
  player: AgentPlayerDetail;
  historyRows: AgentPlayerWalletTransaction[];
  historyLoading: boolean;
};

function txTime(row: AgentPlayerWalletTransaction, timezone: string) {
  const v = row.created_at ?? row.createdAt;
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : formatInTimezone(d, timezone);
}

function toNumber(v: unknown): number {
  if (v == null) return 0;
  const n = typeof v === 'string' ? Number.parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function txSignedAmount(row: AgentPlayerWalletTransaction): number {
  const meta = row.metadata as Record<string, unknown> | null | undefined;
  const delta = meta?.player_credit_delta;
  if (typeof delta === 'number' && Number.isFinite(delta)) return delta;

  const before = toNumber(row.balanceBefore ?? row.balance_before);
  const after = toNumber(row.balanceAfter ?? row.balance_after);
  if (Number.isFinite(before) && Number.isFinite(after)) return after - before;
  return toNumber(row.amount);
}

function fmtCredits(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function txLabel(row: AgentPlayerWalletTransaction): string {
  const meta = row.metadata as Record<string, unknown> | null | undefined;
  const entryKind = String(meta?.entryKind ?? meta?.entry_kind ?? '').toLowerCase();
  if (entryKind === 'transfer_from_shop' || entryKind === 'card_credit_from_shop') {
    return 'Deposit from shop';
  }
  if (entryKind === 'redeem_to_shop') return 'Redeem to shop';

  const t = String(row.type || '').toLowerCase();
  if (t === 'player_deposit') return 'Player deposit';
  if (t === 'distribution') return 'Credit distribution';
  if (t === 'credit_purchase') return 'Credit purchase';
  if (t === 'adjustment') return 'Adjustment';
  return t.replace(/_/g, ' ') || 'Activity';
}

function txBalanceBeforeAfter(row: AgentPlayerWalletTransaction): { before: number | null; after: number | null } {
  const beforeRaw = row.balanceBefore ?? row.balance_before;
  const afterRaw = row.balanceAfter ?? row.balance_after;
  const before = beforeRaw === undefined || beforeRaw === null ? null : toNumber(beforeRaw);
  const after = afterRaw === undefined || afterRaw === null ? null : toNumber(afterRaw);
  const hasBefore = beforeRaw !== undefined && beforeRaw !== null && Number.isFinite(before);
  const hasAfter = afterRaw !== undefined && afterRaw !== null && Number.isFinite(after);
  return {
    before: hasBefore ? before : null,
    after: hasAfter ? after : null,
  };
}

export default function AgentPlayerDetailPanel({ player, historyRows, historyLoading }: Props) {
  const { timezone } = useTimezone();
  const initial =
    (player.username?.trim() || player.phone || '?').charAt(0).toUpperCase();
  const playerBalance = toNumber(player.balance);

  const profileFields = [
    {
      icon: User,
      label: 'Username',
      value: player.username?.trim() || '—',
      mono: false,
    },
    {
      icon: Phone,
      label: 'Phone',
      value: player.phone || '—',
      mono: true,
    },
    {
      icon: Mail,
      label: 'Email',
      value: player.email || '—',
      mono: false,
    },
    {
      icon: Wallet,
      label: 'Balance',
      value: playerBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      mono: true,
      highlight: true,
    },
  ];

  return (
    <div className="space-y-8">
      <section className="overflow-hidden rounded-2xl border border-emerald-100/90 bg-white shadow-sm ring-1 ring-emerald-50">
        <div className="border-b border-emerald-100/80 bg-gradient-to-r from-emerald-50/80 via-white to-teal-50/40 px-4 py-4 sm:px-6 sm:py-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700/80">Player detail</p>
            <h2 className="mt-1 text-lg font-bold tracking-tight text-gray-900 sm:text-xl">Profile</h2>
            {player.shop_id && player.shop_name ? (
              <p className="mt-2 text-sm text-gray-600">
                Shop:{' '}
                <Link
                  to={`/shops/${player.shop_id}`}
                  className="font-medium text-orange-700 hover:text-orange-900 hover:underline"
                >
                  {player.shop_name}
                </Link>
              </p>
            ) : null}
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <div className="flex shrink-0 items-center gap-4 lg:flex-col lg:items-center">
              <div
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 text-2xl font-bold text-white shadow-md ring-4 ring-emerald-100"
                aria-hidden
              >
                {initial}
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="grid gap-3 sm:grid-cols-2">
                {profileFields.map(({ icon: Icon, label, value, mono, highlight }) => (
                  <div
                    key={label}
                    className={`rounded-xl border px-4 py-3 transition ${
                      highlight
                        ? 'border-emerald-200 bg-gradient-to-br from-emerald-50/90 to-white shadow-sm'
                        : 'border-gray-100 bg-gray-50/50 hover:border-emerald-100/80'
                    }`}
                  >
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      <Icon className={`h-3.5 w-3.5 ${highlight ? 'text-emerald-600' : 'text-gray-400'}`} aria-hidden />
                      {label}
                    </div>
                    <p
                      className={`mt-1.5 break-all text-sm font-semibold text-gray-900 ${mono ? 'font-mono tabular-nums' : ''} ${
                        highlight ? 'text-emerald-900' : ''
                      }`}
                    >
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200/90 bg-white p-4 shadow-sm sm:p-6">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-stone-100 text-stone-600">
            <History className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Transaction history</h3>
            <p className="text-xs text-gray-500">Recent activity for this player</p>
          </div>
        </div>

        {historyLoading ? (
          <div className="py-8">
            <PageLoadingIndicator variant="inline" label="Loading…" accentColor="#059669" />
          </div>
        ) : historyRows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 py-10 text-center">
            <p className="text-sm text-gray-500">No transactions for this player yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {historyRows.slice(0, 50).map((row) => {
              const signed = txSignedAmount(row);
              const { before: balBefore, after: balAfter } = txBalanceBeforeAfter(row);
              return (
                <div
                  key={row.id}
                  className="rounded-xl border border-gray-100 bg-gradient-to-r from-gray-50/80 to-white px-4 py-3 text-sm shadow-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-gray-800">{txLabel(row)}</span>
                    <span className={signed >= 0 ? 'font-semibold text-emerald-700' : 'font-semibold text-amber-700'}>
                      {signed >= 0 ? '+' : ''}
                      {signed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500">{txTime(row, timezone)}</p>
                  {balBefore != null || balAfter != null ? (
                    <p className="mt-1.5 text-xs tabular-nums text-gray-600">
                      <span className="text-gray-500">Before</span>{' '}
                      <span className="font-medium text-gray-800">
                        {balBefore != null ? fmtCredits(balBefore) : '—'}
                      </span>
                      <span className="mx-1.5 text-gray-400">→</span>
                      <span className="text-gray-500">After</span>{' '}
                      <span className="font-medium text-gray-800">
                        {balAfter != null ? fmtCredits(balAfter) : '—'}
                      </span>
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
